import { makeStyles, tokens, Button, Text, Menu, MenuTrigger, MenuPopover, MenuList, MenuItem, Tooltip, mergeClasses, Spinner } from '@fluentui/react-components';
import { SubtractRegular, ArrowMaximizeRegular, ArrowMinimizeRegular, ChevronDownRegular, DrawImageRegular, CodeRegular, DesignIdeasRegular, SaveRegular, SaveFilled } from '@fluentui/react-icons';

export type PanelState = 'normal' | 'minimized' | 'maximized';

type DropdownOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

type PanelHeaderProps = {
  title: string;
  state: PanelState;
  onMinimize: () => void;
  onMaximize: () => void;
  onRestore: () => void;
  dropdown?: {
    value: string;
    options: DropdownOption[];
    onChange: (value: string) => void;
  };
  errorBadge?: string | null;
  icon?: 'canvas' | 'code';
  className?: string;
  /** Show a "Show Canvas" action button (used when code panel is maximized) */
  showCanvasAction?: boolean;
  /** Callback when "Show Canvas" is clicked */
  onShowCanvas?: () => void;
  /** Whether workflow has unsaved changes */
  isDirty?: boolean | undefined;
  /** Whether save is in progress */
  isSaving?: boolean | undefined;
  /** Trigger save */
  onSave?: (() => void) | undefined;
};

const useStyles = makeStyles({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: '32px',
    minHeight: '32px',
    padding: `0 ${tokens.spacingHorizontalS}`,
    backgroundColor: tokens.colorNeutralBackground2,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  titleSection: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    minWidth: 0,
  },
  title: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  dropdownTrigger: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3,
    },
  },
  dropdownText: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
  },
  dropdownIcon: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground2,
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
  },
  errorBadge: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase200,
    marginLeft: tokens.spacingHorizontalS,
  },
  minimizedHeader: {
    justifyContent: 'center',
    padding: `0 ${tokens.spacingHorizontalXS}`,
  },
  minimizedButton: {
    minWidth: 'auto',
  },
  saveButton: {
    minWidth: 'auto',
    position: 'relative',
  },
  saveButtonDirty: {
    backgroundColor: tokens.colorBrandBackground2,
    ':hover': {
      backgroundColor: tokens.colorBrandBackground2Hover,
    },
  },
  dirtyIndicator: {
    position: 'absolute',
    top: '2px',
    right: '2px',
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    backgroundColor: tokens.colorPaletteBlueBorderActive,
  },
});

export const PanelHeader = ({
  title,
  state,
  onMinimize,
  onMaximize,
  onRestore,
  dropdown,
  errorBadge,
  icon,
  className,
  showCanvasAction,
  onShowCanvas,
  isDirty,
  isSaving,
  onSave,
}: PanelHeaderProps) => {
  const styles = useStyles();

  const handleMinimizeClick = () => {
    if (state === 'minimized') {
      onRestore();
    } else {
      onMinimize();
    }
  };

  const handleMaximizeClick = () => {
    if (state === 'maximized') {
      onRestore();
    } else {
      onMaximize();
    }
  };

  const getIcon = () => {
    if (icon === 'canvas') return <DrawImageRegular />;
    if (icon === 'code') return <CodeRegular />;
    return null;
  };

  // Minimized state: show only an icon button to restore
  if (state === 'minimized') {
    const panelIcon = getIcon();
    return (
      <div className={mergeClasses(styles.header, styles.minimizedHeader, className)}>
        <Tooltip content={`Expand ${title}`} relationship="label">
          <Button
            icon={panelIcon}
            appearance="subtle"
            size="small"
            onClick={onRestore}
            className={styles.minimizedButton}
            aria-label={`Expand ${title}`}
          />
        </Tooltip>
      </div>
    );
  }

  return (
    <div className={mergeClasses(styles.header, className)}>
      <div className={styles.titleSection}>
        {dropdown ? (
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <button className={styles.dropdownTrigger} aria-label={`Select file, current: ${dropdown.value}`}>
                <span className={styles.dropdownText}>{dropdown.value}</span>
                <ChevronDownRegular className={styles.dropdownIcon} />
              </button>
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                {dropdown.options.map((option) => (
                  <MenuItem
                    key={option.value}
                    disabled={option.disabled ?? false}
                    onClick={() => dropdown.onChange(option.value)}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </MenuList>
            </MenuPopover>
          </Menu>
        ) : (
          <Text className={styles.title}>{title}</Text>
        )}
        {errorBadge && <Text className={styles.errorBadge}>{errorBadge}</Text>}
      </div>
      <div className={styles.controls}>
        {/* Save button - show in code panel when canvas action is visible (code maximized) */}
        {showCanvasAction && onSave && (
          <Tooltip
            content={isSaving ? 'Saving...' : isDirty ? 'Save workflow (Ctrl+S)' : 'No unsaved changes'}
            relationship="label"
          >
            <Button
              className={mergeClasses(
                styles.saveButton,
                isDirty && !isSaving && styles.saveButtonDirty
              )}
              icon={isSaving ? <Spinner size="tiny" /> : isDirty ? <SaveFilled /> : <SaveRegular />}
              appearance="subtle"
              size="small"
              onClick={onSave}
              disabled={!isDirty || !!isSaving}
              aria-label={isSaving ? 'Saving workflow' : 'Save workflow'}
            >
              {isDirty && !isSaving && <span className={styles.dirtyIndicator} />}
            </Button>
          </Tooltip>
        )}
        {showCanvasAction && onShowCanvas && (
          <Tooltip content="Show canvas" relationship="label">
            <Button
              icon={<DesignIdeasRegular />}
              appearance="subtle"
              size="small"
              onClick={onShowCanvas}
              aria-label="Show canvas"
            />
          </Tooltip>
        )}
        {!showCanvasAction && (
          <>
            <Tooltip content="Minimize" relationship="label">
              <Button
                icon={<SubtractRegular />}
                appearance="subtle"
                size="small"
                onClick={handleMinimizeClick}
                aria-label="Minimize panel"
              />
            </Tooltip>
            <Tooltip content={state === 'maximized' ? 'Restore' : 'Maximize'} relationship="label">
              <Button
                icon={state === 'maximized' ? <ArrowMinimizeRegular /> : <ArrowMaximizeRegular />}
                appearance="subtle"
                size="small"
                onClick={handleMaximizeClick}
                aria-label={state === 'maximized' ? 'Restore panel' : 'Maximize panel'}
              />
            </Tooltip>
          </>
        )}
      </div>
    </div>
  );
};
